import AuthApi from "@apis/auth.api";
import { reflectKeys } from "@utils/key.reflactor";
import { redirect } from "@plugins/router";
import { isDefined, isEmpty } from "@utils/var.check";
import ROLE from "@configs/role";
import PERMISSION from "@configs/permission";
import LOGIN_FLOW from "@configs/loginFlow";
import LOGIN_TYPE from "@configs/loginType";
import cryptoUtils from "@utils/crypto.utils";
import store from "@plugins/store";
import appConfigs from "@configs/app";
import { resourceSignedCookieService } from "@/modules/auth/service/resource.signed.cookie.service";
import { authStateChannel } from "@/modules/auth/service/auth.state.channel";

/**
 * Auth store
 * @description implementation reference from nuxtjs auth module
 * @link https://auth.nuxtjs.org/api/auth.html
 */

let lastAuthCheck: number | null = null;
// TODO: SLS-9261 revisit the logic for auth check for redirecting to homepage after login
const AUTH_CHECK_MAX_WAIT = 300000; // 5 minutes

/**
 * NOTE: Don't map these state in your components, use mapGetters instead
 */
const state = () => ({
  logged: 0,
  user: null,
  userAvatarFile: null,
  switchableAccount: null,
  canAdministrate: false,
  hasAdministratedSchool: false,
  loggedInFromSls: false,
  publicKey: null,
  trackingId: null,
  roles: {},
  permissions: {},
  currentAcademicYear: null,
  loginFlow: null,
});

const getters = {
  isLogged: (state) => state.logged,
  isLoggedInFromSls: (state) => state.loggedInFromSls,
  isEdupassLogin: (state) => {
    return state.loginFlow === LOGIN_FLOW.EDUPASS_LOGIN;
  },
  user: (state) => state.user,
  userAvatarFile: (state) => state.userAvatarFile,
  userId: (state) => {
    const userObj = state.user;
    return userObj ? userObj.id : "";
  },
  userInfoId: (state) => {
    const userObj = state.user;
    return userObj ? userObj.userInfoId : "";
  },
  isAssociatedAccount: (state) => {
    return state.switchableAccount !== "";
  },
  isTeacherAssociatedStudent: (state) => {
    return state.switchableAccount === "teacherAccount";
  },
  isCurrentAccountTeacherAccount: (state) => {
    return state.switchableAccount === "studentAccount";
  },
  publicKey: (state) => state.publicKey,
  trackingId: (state) => state.trackingId,
  userDisplayName: (state) => {
    const userObj = state.user;
    const userDisplayName = userObj ? userObj.displayName : "";
    return userDisplayName ? userDisplayName : userObj.name;
  },
  userSystemName: (state) => {
    const userObj = state.user;
    const userName = userObj ? userObj.name : "";
    return userName ? userName : "";
  },
  //returns formatted name if display name exists, otherwise return user name
  userFormattedName: (state) => {
    const userObj = state.user;
    const userName = userObj ? userObj.name : "";
    const userDisplayName = userObj ? userObj.displayName : "";
    if (isEmpty(userDisplayName)) {
      return userName;
    } else {
      return `${userDisplayName} (${userName})`;
    }
  },
  userRoles: (state) => state.roles,
  hasRole: (state, getters) => (roles) => {
    const userRoles = Object.keys(getters.userRoles);
    if (Array.isArray(roles)) {
      return isDefined(
        roles.find((role) => {
          return userRoles.includes(role);
        }),
      );
    }
    return userRoles.includes(roles);
  },
  userPermissions: (state) => state.permissions,
  currentAcademicYear: () => {
    return store.dispatch("app/fetchCurrentAcademicYear").then((response) => response);
  },
  userInfoFormatted: (state) => {
    if (!state.user) return null;

    let loginType = "";
    // Set loginType to 'SLS' or 'EDUPASS' based on the loginflow
    if (state.loggedInFromSls) {
      loginType = LOGIN_TYPE.SLS;
    } else if (state.loginFlow === LOGIN_FLOW.EDUPASS_LOGIN) {
      loginType = LOGIN_TYPE.EDUPASS;
    }

    return {
      trackingId: state.trackingId,
      userId: state.user.uuid,
      userName: state.user.name,
      schoolCode: state.user.schoolCode || "",
      formClass: state.user.formClass || null, // Applicable to student only
      levelAndStream: state.user.levelAndStream || null, // Applicable to student only
      academicYear: store.getters["app/getCurrentAcademicYear"],
      loginType: loginType,
    };
  },
  hasPermission: (state, getters) => (permissions) => {
    const userPermission = Object.keys(getters.userPermissions);
    if (Array.isArray(permissions)) {
      return isDefined(
        permissions.find((permission) => {
          return userPermission.includes(permission);
        }),
      );
    }
    return userPermission.includes(permissions);
  },
  isPermissionsOutdated: (state, getters) => (userObj) => {
    const existingPermissions = Object.keys(getters.userPermissions);
    const newPermissions = userObj.permissions;
    let difference;
    if (newPermissions.length >= existingPermissions.length) {
      difference = newPermissions.filter((x) => !existingPermissions.includes(x));
    } else {
      difference = existingPermissions.filter((x) => !newPermissions.includes(x));
    }
    return difference.length > 0;
  },
  isRolesOutdated: (state, getters) => (userObj) => {
    const existingRoles = Object.keys(getters.userRoles);
    const newRoles = userObj.roles;
    let difference;
    if (newRoles.length >= existingRoles.length) {
      difference = newRoles.filter((x) => !existingRoles.includes(x));
    } else {
      difference = existingRoles.filter((x) => !newRoles.includes(x));
    }
    return difference.length > 0;
  },
  isStudent: (state, getters) => {
    return getters.hasRole([ROLE.STUDENT]);
  },
  isTeacher: (state, getters) => {
    return getters.hasRole([ROLE.TEACHER]);
  },
  isSchoolAdmin: (state, getters) => {
    return getters.hasRole([ROLE.SCHOOL_ADMIN]);
  },
  isCA: (state, getters) => {
    return getters.hasRole([ROLE.CONTENT_APPROVER]);
  },
  isCO: (state, getters) => {
    return getters.hasRole([ROLE.CONTENT_OFFICER]);
  },
  isAlpAdmin: (state, getters) => {
    return getters.hasRole([ROLE.ALP_ADMIN]);
  },
  isHelpdeskAdmin: (state, getters) => {
    return getters.hasRole([ROLE.HELPDESK_ADMIN]);
  },
  isAppAdmin: (state, getters) => {
    return getters.hasRole([ROLE.APP_ADMIN]);
  },
  isCCPMAdmin: (state, getters) => {
    return getters.hasRole([ROLE.CCPM_ADMIN]);
  },
  isVLEAdmin: (state, getters) => {
    return getters.hasRole([ROLE.VLE_ADMIN]);
  },
  isAuthorityAdmin: (state, getters) => {
    return getters.hasRole([ROLE.AUTHORITY_ADMIN]);
  },
  isAccountManager: (state, getters) => {
    return getters.hasRole([ROLE.ACCOUNT_MANAGER]);
  },
  isOtpLogin: (state, getters) => {
    return getters.hasRole([PERMISSION.OTP_LOGIN]);
  },
  canAdministrate: (state) => {
    return state.canAdministrate;
  },
  isAdmin: (state, getters) => {
    return getters.hasRole([
      ROLE.ALP_ADMIN,
      ROLE.HELPDESK_ADMIN,
      ROLE.CONTENT_OFFICER,
      ROLE.CONTENT_APPROVER,
      ROLE.SCHOOL_ADMIN,
      ROLE.APP_ADMIN,
      ROLE.VLE_ADMIN,
      ROLE.CCPM_ADMIN,
      ROLE.AUTHORITY_ADMIN,
      ROLE.ACCOUNT_MANAGER,
    ]);
  },
  isPrivilegeUser: (state, getters) => {
    return getters.hasRole([
      ROLE.SCHOOL_ADMIN,
      ROLE.HELPDESK_ADMIN,
      ROLE.APP_ADMIN,
      ROLE.VLE_ADMIN,
      ROLE.CCPM_ADMIN,
      ROLE.AUTHORITY_ADMIN,
      ROLE.ACCOUNT_MANAGER,
    ]);
  },
  isUserLimitedAccess: (state) => {
    return state.user?.limitedAccess;
  },
};

const mutations = {
  setPublicKey(state, { publicKey }) {
    state.publicKey = publicKey;
  },
  setTrackingId(state, { trackingId }) {
    state.trackingId = trackingId;
  },
  setName(state, { userDisplayName }) {
    state.user.displayName = userDisplayName;
  },
  setUser(state, { userObj }) {
    state.user = userObj;
  },
  setUserAvatarFile(state, { userAvatarFile }) {
    state.userAvatarFile = userAvatarFile;
  },
  setPermissions(state, { userObj }) {
    state.permissions = reflectKeys(userObj.permissions);
  },
  setRoles(state, { userObj }) {
    state.roles = reflectKeys(userObj.roles);
  },
  setLogged(state) {
    state.logged = 0;
  },
  setCanAdministrate(state, canAdministrate) {
    state.canAdministrate = canAdministrate;
  },
  setHasAdministratedSchool(state, hasAdministratedSchool) {
    state.hasAdministratedSchool = hasAdministratedSchool;
  },
  setLoggedInFromSls(state, loggedInFromSls) {
    state.loggedInFromSls = loggedInFromSls;
  },
  setLoginFlow(state, loginFlow) {
    state.loginFlow = loginFlow;
  },
  login(
    state,
    {
      userObj,
      switchableAccount,
      canAdministrate,
      hasAdministratedSchool,
      loggedInFromSls,
      trackingId,
      loginFlow,
    },
  ) {
    state.logged = 1;
    state.user = userObj;
    state.userAvatarFile = userObj.avatarFilename.startsWith("default")
      ? `${appConfigs.s3ResourceDomain}/avatar/user/${
          userObj.avatarFilename
        }.svg?t=${new Date().getTime()}`
      : `${appConfigs.s3ResourceDomain}/avatar/user/${userObj.avatarFilename}/${
          userObj.avatarFileVersion
        }/${userObj.avatarFilename}.svg?t=${new Date().getTime()}`;
    state.switchableAccount = switchableAccount;
    state.canAdministrate = canAdministrate;
    state.hasAdministratedSchool = hasAdministratedSchool;
    state.loggedInFromSls = loggedInFromSls;
    state.trackingId = trackingId;
    state.loginFlow = loginFlow;

    state.roles = reflectKeys(userObj.roles);
    state.permissions = reflectKeys(userObj.permissions);

    // SLS JS Object
    window._SLS = window._SLS || {};
  },
  logout(state) {
    state.logged = 0;
    state.user = null;
    state.userAvatarFile = null;
    state.switchableAccount = null;
    state.canAdministrate = false;
    state.hasAdministratedSchool = false;
    state.loggedInFromSls = false;
    state.roles = {};
    state.permissions = {};
    state.trackingId = null;
    state.loginFlow = null;

    // SLS JS Object
    window._SLS = window._SLS || {};

    authStateChannel.logout(); // Notify other tabs
  },
};

const actions = {
  authCheck({ commit, getters }, forceCheck = false) {
    return new Promise((resolve, reject) => {
      // reduce the frequency of auth check, at most 30s check once
      if (
        !forceCheck &&
        lastAuthCheck != null &&
        Date.now() - lastAuthCheck < AUTH_CHECK_MAX_WAIT
      ) {
        if (getters.isLogged) {
          resolve();
        } else {
          reject();
        }
        return;
      }

      AuthApi.authCheck().then((response) => {
        if (response && response.data && response.data.logged) {
          // Signed cookie failure must not fail the auth check
          resourceSignedCookieService
            .getResourceSignedCookie()
            .catch(() => undefined)
            .then(() => {
              const userObj = response.data.user;
              const loginFlow = response.data.loginFlow;
              const switchableAccount = response.data.switchableAccount;
              const publicKey = response.data.publicKey;
              const trackingId = response.data.trackingId;
              const canAdministrate = response.data.canAdministrate;
              const hasAdministratedSchool = response.data.hasAdministratedSchool;
              const loggedInFromSls =
                response.data.loginFlow === LOGIN_FLOW.SLS_PASSWORD_LOGIN ||
                response.data.loginFlow === LOGIN_FLOW.SLS_OTP_LOGIN;
              commit("setPublicKey", { publicKey });
              cryptoUtils.importPublicKey(publicKey);
              if (!getters.isLogged) {
                // if not logged, just commit, user is now logged
                commit("login", {
                  userObj,
                  switchableAccount,
                  canAdministrate,
                  hasAdministratedSchool,
                  loggedInFromSls,
                  trackingId,
                  loginFlow,
                });
              } else if (getters.user.id !== userObj.id) {
                // user changed, potentially all state will be out of sync, reload the page
                commit("logout");
                redirect(window.location.href);
              } else if (
                getters.isPermissionsOutdated(userObj) ||
                getters.isRolesOutdated(userObj)
              ) {
                // still same user and still logged in, reset permissions if they have changed
                const userAvatarFile = userObj.avatarFilename.startsWith("default")
                  ? `${appConfigs.s3ResourceDomain}/avatar/user/${
                      userObj.avatarFilename
                    }.svg?t=${new Date().getTime()}`
                  : `${appConfigs.s3ResourceDomain}/avatar/user/${userObj.avatarFilename}/${
                      userObj.avatarFileVersion
                    }/${userObj.avatarFilename}.svg?t=${new Date().getTime()}`;
                commit("setPermissions", { userObj });
                commit("setRoles", { userObj });
                commit("setUser", { userObj });
                commit("setUserAvatarFile", { userAvatarFile });
                commit("setCanAdministrate", canAdministrate);
                commit("setHasAdministratedSchool", hasAdministratedSchool);
                commit("setTrackingId", trackingId);
                commit("setLoggedInFromSls", loggedInFromSls);
                commit("setLoginFlow", loginFlow);
              }
              resolve();
              lastAuthCheck = Date.now();
            });
        } else {
          // Set Public Key even without logging in for encrypting password during reset password
          if (response) {
            const publicKey = response.data.publicKey;
            commit("setPublicKey", { publicKey });
            cryptoUtils.importPublicKey(publicKey);
          }

          commit("logout");
          reject();
          lastAuthCheck = Date.now();
        }
      });
    });
  },
  updateName({ commit }, userDisplayName) {
    commit("setName", { userDisplayName });
  },
  updateLogged({ commit }) {
    commit("setLogged");
  },
  invalidateLastCheck() {
    // to allow force check
    lastAuthCheck = null;
  },
  reRenderUserAvatar({ commit }, data) {
    const fileName = data.fileName;
    const fileVersion = data.fileVersion;
    const userAvatarFile = fileName.startsWith("default")
      ? `${appConfigs.s3ResourceDomain}/avatar/user/${fileName}.svg?t=${new Date().getTime()}`
      : `${
          appConfigs.s3ResourceDomain
        }/avatar/user/${fileName}/${fileVersion}/${fileName}.svg?t=${new Date().getTime()}`;
    commit("setUserAvatarFile", { userAvatarFile });
  },
};

export default {
  namespaced: true,
  state,
  getters,
  mutations,
  actions,
};
